import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getOnboardingSteps, completeOnboardingStep, OnboardingData } from '@app/services/onboarding';
import { useNavigationContext } from '@app/contexts/NavigationContext';
import { toast } from 'react-toastify';

export const useOnboardingPage = () => {
  const { interceptNavigation } = useNavigationContext();
  const queryClient = useQueryClient();

  const { data: onboardingData, isLoading, error } = useQuery<OnboardingData>({
    queryKey: ['onboarding-steps'],
    queryFn: async () => {
      const response = await getOnboardingSteps(true); // Show completed steps
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const completeStepMutation = useMutation({
    mutationFn: (stepId: string) => completeOnboardingStep(stepId, true),
    onSuccess: () => {
      // Refetch the onboarding data to update the UI
      queryClient.invalidateQueries({ queryKey: ['onboarding-steps'] });
    },
    onError: (error) => {
      console.error('Error completing step:', error);
      toast.error('Failed to mark step as completed. Please try again.');
    },
  });

  const handleStepAction = (stepId: string) => {
    // Mark the step as completed
    completeStepMutation.mutate(stepId);
  };

  const handleStepNavigation = (stepId: string) => {
    // Find the step in the current data to get its redirectUrl
    const step = onboardingData?.steps?.find(s => s.id === stepId);
    if (step?.redirectUrl) {
      interceptNavigation(step.redirectUrl);
    }
  };

  return {
    onboardingData,
    isLoading,
    error,
    handleStepAction,
    handleStepNavigation,
    isCompletingStep: completeStepMutation.isPending,
  };
}; 